import { ChangeDetectionStrategy, ChangeDetectorRef, Component, EventEmitter, Input, OnDestroy, OnInit, Output } from '@angular/core';
import { NavController } from '@ionic/angular';
import { BehaviorSubject, distinctUntilChanged, EMPTY, iif, map, Subject, switchMap, takeUntil, timer } from 'rxjs';
import { AppRoutes } from '../../../app-routes';
import { DynamicPromoPage } from '../../../modals/dynamic-promo/dynamic-promo.page';
import { LadderPromoPage } from '../../../modals/ladder-promo/ladder-promo.page';
import { MaxDiscountsLimitPage } from '../../../modals/max-discounts-limit/max-discounts-limit.page';
import { IAnalyticsListContext } from '../../../models/Analytics';
import { IPromosEcommerce } from './../../../../private/shared/models/productos';
import { ModalsService } from './../../../services/modals/modals.service';

@Component({
  selector: 'getm-promo-card-v2',
  templateUrl: './promo-card-v2.component.html',
  styleUrls: ['./promo-card-v2.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class PromoCardV2Component implements OnInit, OnDestroy {

  @Input() set promo(value: IPromosEcommerce) {
    this.promoData = value;
    this.quantity = value.PaquetesCanjeados;
    this.claveProducto = value.Productos.length > 0 ? value.Productos[0].clave_producto : 0;
    this.extrasQuantity();
  }
  @Input() analyticsContext?: IAnalyticsListContext;

  @Output() quantityChange = new EventEmitter<{ ClavePromo: string, quantity: number }>();
  @Output() quantityExtrasChange = new EventEmitter<{ ClavePromo: string, clave_producto: number, quantity: number }>();
  @Output() promoClick = new EventEmitter<IPromosEcommerce>();

  public promoData: IPromosEcommerce = {} as IPromosEcommerce;
  public quantity: number = 0;
  public quantityExtras$ = new BehaviorSubject<number>(0);
  public claveProducto: number = 0;
  public activeSlide: number = 0;

  private emitirExtras: boolean = false;
  private finishSubscriptions: Subject<boolean> = new Subject();

  constructor(
    private modalsS: ModalsService
    , private cdr: ChangeDetectorRef
    , private navCtrl: NavController
  ) { }

  ngOnInit() {
    this.quantityExtras$
      .pipe(
        distinctUntilChanged((prev, curr) => (prev > 0) === (curr > 0)),
        switchMap(quantity =>
          iif(() => quantity > 0,
            timer(3000, 3000).pipe(map((tick) => (tick + 1) % 2)),
            EMPTY
          )
        ),
        takeUntil(this.finishSubscriptions)
      )
      .subscribe(this.callBackStartAutoPlay);
  }

  ngOnDestroy() {
    this.finishSubscriptions.next(true);
    this.finishSubscriptions.complete();
  }

  public onBuildPromo = async () => {
    if (this.promoData.MaximoPaquetes - this.promoData.PaquetesCanjeados < 0) return;
    switch (this.promoData.TipoPromocion) {
      case 'ESTATICO':
        this.quantity = 1;
        this.quantityChange.emit({ ClavePromo: this.promoData.ClavePromo, quantity: this.quantity });
        break;
      case 'ESCALERA':
        await this.modalsS.modalDetailPromo(LadderPromoPage, this.promoData, this.analyticsContext);
        break;
      case 'DINAMICO':
        await this.modalsS.modalDetailPromo(DynamicPromoPage, this.promoData, this.analyticsContext);
        break;
      case 'BONIFICACION':
        await this.modalsS.modalDetailPromo(DynamicPromoPage, this.promoData, this.analyticsContext);
        break;
    }
  }

  public onQuantityChange = async (quantity: number) => {
    const oldQuantity = this.quantity;
    this.quantity = quantity;
    if (this.quantity === this.promoData.MaximoPaquetes + 1 && oldQuantity === this.promoData.MaximoPaquetes) {
      const accept = await this.modalsS.presentModalMaxDiscountsLimit(MaxDiscountsLimitPage);
      if (!accept) {
        this.quantity = this.promoData.MaximoPaquetes;
        this.cdr.markForCheck();
        return;
      }
    }

    this.quantityChange.emit({ ClavePromo: this.promoData.ClavePromo, quantity });
    this.extrasQuantity();
    if (this.emitirExtras) this.quantityExtrasChange.emit({ ClavePromo: this.promoData.ClavePromo, clave_producto: this.claveProducto, quantity: this.quantityExtras$.value });
  }

  public onPromoClick = (): void => {
    if (this.promoData.TipoPromocion !== 'ESTATICO') return;
    this.promoClick.emit(this.promoData);
    if (this.promoData?.ClavePromo) {
      this.navCtrl.navigateForward(`/${AppRoutes.PROMO_DETAIL}/${this.promoData.ClavePromo}`);
    }
  }

  public trackByMarca = (_: number, marca: { Marca: string }): string => marca.Marca;

  private callBackStartAutoPlay = (slideIndex: number) => {
    // markForCheck necesario bajo OnPush: el cambio viene de un timer interno.
    this.activeSlide = slideIndex;
    this.cdr.markForCheck();
  };

  private extrasQuantity = () => {
    this.emitirExtras = false;
    const isEstaticoExtras = this.promoData.TipoPromocion === 'ESTATICO' && this.promoData.Productos.length === 1;
    if (isEstaticoExtras && this.quantity > this.promoData.MaximoPaquetes) {
      this.quantityExtras$.next(this.quantity - this.promoData.MaximoPaquetes);
      this.emitirExtras = true;
    } else if (isEstaticoExtras && this.quantity <= this.promoData.MaximoPaquetes && this.quantityExtras$.value > 0) {
      this.quantityExtras$.next(0);
      this.emitirExtras = true;
    }
  }

}
